import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../hooks/useAuthStore'
import { PageHeader } from '../components/PageHeader'
import { DashboardCard } from '../components/DashboardCard'
import apiService from '../services/api'

// Local formatters
const formatThaiCurrency = (amount: number) => {
  return new Intl.NumberFormat('th-TH', {
    style: 'currency',
    currency: 'THB',
    minimumFractionDigits: 0
  }).format(amount)
}

interface LastEnrollment {
  id: number
  student_name: string
  nickname: string
  course_id: number
  course_name: string
  course_price: number
  course_schedule: string
  course_subjects: string[]
  month_year: string
  payment_status: string
}

export const CourseRenewalPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [lastEnrollment, setLastEnrollment] = useState<LastEnrollment | null>(null)
  const [loading, setLoading] = useState(true)
  const [renewing, setRenewing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Get next month for renewal
  const nextDate = new Date()
  nextDate.setMonth(nextDate.getMonth() + 1)
  const renewalMonth = `${nextDate.getFullYear()}-${(nextDate.getMonth() + 1).toString().padStart(2, '0')}`

  useEffect(() => {
    loadLastEnrollment()
  }, [])

  const loadLastEnrollment = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      const enrollment = await apiService.getLastEnrollment(user.id)
      setLastEnrollment(enrollment)
    } catch (err) {
      setError('ไม่สามารถโหลดข้อมูลการลงทะเบียนได้')
      console.error('Failed to load last enrollment:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleRenewal = async () => {
    if (!user?.id || !lastEnrollment) return

    try {
      setRenewing(true)
      
      const renewal = await apiService.renewEnrollment(user.id, renewalMonth)
      
      // Navigate to payment instructions
      navigate('/enrollment/payment', {
        state: {
          enrollment: renewal,
          course: {
            id: lastEnrollment.course_id,
            name: lastEnrollment.course_name,
            price: lastEnrollment.course_price,
            schedule: lastEnrollment.course_schedule
          },
          from: 'renewal'
        }
      })

    } catch (error) {
      console.error('Renewal failed:', error)
      setError('การต่ออายุล้มเหลว กรุณาลองใหม่อีกครั้ง')
    } finally {
      setRenewing(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader title="ต่ออายุคอร์สเรียน" />
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">กำลังโหลดข้อมูล...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !lastEnrollment) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PageHeader 
          title="ต่ออายุคอร์สเรียน" 
          showBackButton
          onBack={() => navigate('/dashboard')}
        />
        <div className="max-w-2xl mx-auto px-4 py-6">
          <div className="text-center py-12">
            <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              ไม่พบประวัติการลงทะเบียน
            </h2>
            <p className="text-gray-600 mb-8">
              คุณยังไม่ได้ลงทะเบียนคอร์สเรียนมาก่อน<br />
              กรุณาลงทะเบียนคอร์สใหม่แทน
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/courses')}
                className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                ลงทะเบียนคอร์สใหม่
              </button>
              <div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="text-gray-600 hover:text-gray-800 underline"
                >
                  กลับสู่หน้าหลัก
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader 
        title="ต่ออายุคอร์สเรียน" 
        showBackButton
        onBack={() => navigate('/dashboard')}
      />
      
      <div className="max-w-2xl mx-auto px-4 py-6">
        {/* Current Course Info */}
        <DashboardCard title="คอร์สที่กำลังเรียน" className="mb-6">
          <div className="space-y-4">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="font-semibold text-gray-900">{lastEnrollment.course_name}</h3>
                <p className="text-sm text-gray-600 mt-1">{lastEnrollment.course_schedule}</p>
              </div>
              <div className="text-right">
                <div className="text-lg font-bold text-blue-600">
                  {formatThaiCurrency(lastEnrollment.course_price)}
                </div>
                <div className="text-sm text-gray-500">ต่อเดือน</div>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-1">
              {lastEnrollment.course_subjects.map((subject, index) => (
                <span
                  key={index}
                  className="inline-block px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                >
                  {subject}
                </span>
              ))}
            </div>

            <div className="pt-3 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">นักเรียน:</span>
                  <div className="font-medium">{lastEnrollment.student_name}</div>
                  <div className="text-gray-500">({lastEnrollment.nickname})</div>
                </div>
                <div>
                  <span className="text-gray-600">เดือนปัจจุบัน:</span>
                  <div className="font-medium">{lastEnrollment.month_year}</div>
                </div>
              </div>
            </div>
          </div>
        </DashboardCard>

        {/* Renewal Confirmation */}
        <DashboardCard title="ต่ออายุสำหรับเดือนหน้า">
          <div className="space-y-6">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center mb-3">
                <svg className="w-5 h-5 text-green-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="font-medium text-green-900">การต่ออายุอัตโนมัติ</span>
              </div>
              <div className="text-sm text-green-800 space-y-2">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-green-700">คอร์ส:</span>
                    <div className="font-medium">{lastEnrollment.course_name}</div>
                  </div>
                  <div>
                    <span className="text-green-700">เดือนที่ต่ออายุ:</span>
                    <div className="font-medium">{renewalMonth}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-green-700">นักเรียน:</span>
                    <div className="font-medium">{lastEnrollment.nickname}</div>
                  </div>
                  <div>
                    <span className="text-green-700">ค่าเรียน:</span>
                    <div className="font-bold text-green-600">
                      {formatThaiCurrency(lastEnrollment.course_price)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-sm text-gray-600 space-y-2">
              <h4 className="font-medium text-gray-900">ข้อมูลการต่ออายุ:</h4>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>ระบบจะใช้ข้อมูลจากการลงทะเบียนครั้งล่าสุด</li>
                <li>คอร์สและข้อมูลนักเรียนจะเหมือนเดิม</li>
                <li>ค่าเรียนอาจเปลี่ยนแปลงตามประกาศของทางโรงเรียน</li>
                <li>หลังจากต่ออายุจะต้องชำระเงินเพื่อยืนยันการเรียน</li>
              </ul>
            </div>

            <div className="flex space-x-4 pt-6">
              <button
                onClick={() => navigate('/courses')}
                className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                เลือกคอร์สใหม่แทน
              </button>
              <button
                onClick={handleRenewal}
                disabled={renewing}
                className={`flex-1 py-3 px-4 rounded-lg font-medium text-white transition-colors ${
                  renewing
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {renewing ? 'กำลังต่ออายุ...' : 'ยืนยันต่ออายุ'}
              </button>
            </div>
          </div>
        </DashboardCard>

        {/* Important Notes */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium text-blue-900 mb-2">ข้อมูลสำคัญ</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• การต่ออายุจะมีผลเมื่อเจ้าหน้าที่ตรวจสอบการชำระเงินแล้ว</li>
            <li>• หากต้องการเปลี่ยนคอร์ส กรุณาเลือก "เลือกคอร์สใหม่แทน"</li>
            <li>• สามารถยกเลิกการต่ออายุได้ก่อนชำระเงิน</li>
            <li>• หากมีปัญหา กรุณาติดต่อเจ้าหน้าที่</li>
          </ul>
        </div>
      </div>
    </div>
  )
}